import { cva } from 'class-variance-authority';
import { focusRing } from 'components/styles/focusRing';

/**
 * Accordion class recipes.
 *
 * Like `Button.recipe` and `TextArea.recipe`, these contain ZERO `dark:` variants — colour comes
 * entirely from the `--ds-accordion-*` slots that `.ds-accordion` sets in
 * `components/styles/design-system.css`, and that class needs no dark rule at all because every
 * slot it sets names a semantic role and a role already carries a value per theme.
 *
 * ── Ported from Figma 1447:135303 on 2026-09-18, reworked 2026-09-22 ──────────────────────────
 *
 * An earlier revision of this file recorded that there was no Design System 2.0 drawing of this
 * component and that its structure and colour were "open to the design review". That review has
 * happened: the set exists, it carries a written description that names this directory, and the
 * geometry below is read off the cells rather than chosen. Where a value here disagrees with the
 * previous implementation, the drawing is the reason.
 *
 * The set was reworked on 2026-09-22 and the `Surface` axis collapsed from three values to two.
 * `On surface` — the translucent tint that used to be the default — was DELETED, `On page` was
 * renamed `Filled`, and `Filled` is now the default. Three consequences reach this file:
 *
 *   1. `surface` is `'filled' | 'none'`. A hard rename, no aliases.
 *   2. `isOpen` carries no colour at all. Fill and rim are identical open and closed on every
 *      surface; only the root gap changes. The two `[data-open]` paint rules are gone.
 *   3. The interaction overlay on `Filled` is now OPAQUE `background/primary/hover` and
 *      `/active`. That is what forced the overlay off the item and onto the trigger — see
 *      `accordionTriggerRecipe`.
 *
 * The five axes the set draws, and where each one is answered:
 *
 *   Surface   colour only — `data-surface` on the root, resolved to slots in `design-system.css`.
 *   Layout    geometry only — a `layout` variant on the recipes below.
 *   State     the trigger's hover, active and focus-visible pseudo-classes, painted on the TRIGGER
 *             for the fill and on the ITEM for the rim. The split is the drawing's — see below.
 *   isOpen    `[data-open]` on the item, which is now GEOMETRY ONLY; the trigger uses
 *             `[data-panel-open]` for its bottom inset, its squared corners and its child glyphs.
 *   isDisabled `[data-disabled]`, likewise. See the note further down on where it arrives from.
 *
 * Nothing is set by two of them, which is the set description's own claim and is worth preserving:
 * it is what lets each axis be a flat variant rather than a matrix.
 *
 * ── Where the state comes from, and why it is not `TextArea`'s shape ───────────────────────────
 *
 * The field pair reads `Field.Root`'s `data-focused` / `data-filled` / `data-invalid` through a
 * `group/ti` marker, because there the element that KNOWS the state and the element that draws it
 * are different. Here the trigger is the control, so its own pseudo-classes are the state, and the
 * rules take Button's guard form:
 *
 *   [&:not([data-disabled]):not(:active):hover]   hover, excluded from pressed and disabled
 *   [&:not([data-disabled]):active]               pressed
 *   [&[data-disabled]]                            disabled
 *
 * The guards matter for exactly the reason `Button.recipe` spells out: a MOUSE press matches
 * `:hover` and `:active` at once, the two rules compile to the same specificity, and without
 * `:not(:active)` the winner is decided by whatever order Tailwind happens to emit them in.
 *
 * Two groups are declared. `group/accordion` is on the root and carries the three container-level
 * choices — surface, layout and icon placement — down to the parts that need them as selectors
 * rather than as props. `group/accordion-trigger` is on the trigger and is read by the indicator,
 * the title and the description, which are children with no state of their own.
 *
 * ── The `data-disabled` the trigger carries ───────────────────────────────────────────────────
 *
 * Base UI 1.7.0 builds the trigger with `focusableWhenDisabled: true`, so a disabled row is NOT
 * given the native `disabled` attribute: it keeps its place in the tab order and is marked with
 * `aria-disabled` instead. That is the accessible behaviour — a control that vanishes from the tab
 * order is a control a keyboard user cannot discover the state of — and it is also why every rule
 * below keys off `data-disabled` rather than `:disabled`, which would never match.
 *
 * ── How disabled is drawn, which changed in this port ──────────────────────────────────────────
 *
 * The previous implementation swapped the card's fill to `--background-disabled` and its rim to
 * `--border-control-disabled`, then dropped the title and icon to `--foreground-disabled`. The set
 * does none of that: the container KEEPS its enabled fill and rim, and the CONTENT drops to 50%
 * opacity.
 *
 * That is not a shortcut, and the set description gives the measurement behind it:
 * `background/disabled` is opaque and tuned to a page-level host, so on a tinted card it
 * measures 1.26:1 in dark — HEAVIER than the enabled fill, i.e. the "disabled" treatment made the
 * card more prominent — and 1.00:1 in light, i.e. invisible. One opacity value works on every host
 * in both modes, and it also fades a `Tag` in the left slot without needing a disabled Tag cell,
 * which the Tag set does not have.
 *
 * WCAG 1.4.3 exempts disabled controls from the contrast floor, so the 50% is legitimate rather
 * than tolerated. `accordionContrast.test.ts` asserts the exemption explicitly rather than
 * measuring through it.
 */

/**
 * The list.
 *
 * The `.ds-accordion` slot class lives HERE rather than on the item, for the reason
 * `textAreaRootRecipe` documents: custom properties cascade, so everything inside inherits them,
 * and anything that later renders outside the item — a list-level empty state, say — would
 * otherwise fall outside their scope and resolve to inherited colour rather than failing visibly.
 *
 * `group/accordion` carries surface, layout and icon placement down to the parts, so those choices
 * are made once on the container and read as selectors rather than threaded through React context.
 * That keeps them in the recipe layer, which is where a visual decision belongs.
 *
 * ── The gap, and the one surface that has none ─────────────────────────────────────────────────
 *
 * 8px between items on the boxed surface, which is the set description's own instruction
 * ("stack instances 8px apart (x2) to make a group") and also what the previous implementation
 * used. The gap rather than a collapsed shared seam: the seam is the stacked-FIELD contract, and it
 * is wrong here, because a field group is one control split into rows while an accordion list is
 * several independent cards. The gap also gives the whole-item focus shadow somewhere to be drawn —
 * see `accordionItemRecipe`.
 *
 * `surface="none"` takes NO gap, because it has no cards to separate. The set's rule is that such a
 * group takes a `Divider` between rows instead of more air, and the divider has to be supplied by
 * the caller — a component that cannot see its own siblings cannot interleave anything between
 * them. Two cautions from the set, both worth repeating at the call site: use the reviewed
 * `Divider`, which binds the translucent `--border-on-surface` and so is visible where
 * `--border-primary` would not be; and zero its horizontal padding, which is 12px despite its own
 * description claiming full-bleed.
 */
export const accordionRootRecipe = cva(['ds-accordion group/accordion', 'flex w-full flex-col'], {
  variants: {
    surface: {
      filled: 'gap-[8px]',
      none: 'gap-0',
    },
  },
  defaultVariants: { surface: 'filled' },
});

/**
 * One section: the card that holds a row and its panel.
 *
 * NOT `overflow-hidden`, which is the obvious thing to reach for on a rounded box containing an
 * animating child and is wrong twice over. The clipping the height animation needs belongs on the
 * panel, which is the element whose height changes; and clipping here would cut the item's focus
 * shadow off at the card's edge, leaving a focus indicator visible on three sides.
 *
 * ── Radius ────────────────────────────────────────────────────────────────────────────────────
 *
 * Read off the set: `radius/lg` (24px) for stacked, `radius/md` (16px) for inline. `surface="none"`
 * KEEPS the inline 16px even with nothing to fill, because the focus halo is a box-shadow and
 * traces the item's own corners. The two boxed radii pair with the two insets — the taller card
 * with the wider inset — which is why the axis is geometry and not decoration.
 *
 * ── The rim, and the three rungs it climbs ────────────────────────────────────────────────────
 *
 * The item owns the RIM. The trigger owns the FILL. That split is the drawing's rather than a
 * convenience: in the reworked set the interaction overlay is a layer sized to the clickable row,
 * while the stroke that steps with it is on the variant ROOT. See `accordionTriggerRecipe` for the
 * fill half and why an opaque overlay made the split load-bearing.
 *
 *   rest      --ds-accordion-stroke
 *   hover     --ds-accordion-stroke-hover
 *   active    --ds-accordion-stroke-active     outranks hover, so a pressed row reads pressed
 *   open      unchanged — `isOpen` carries NO colour in the reworked set
 *   focus     unchanged — the shared halo carries focus on its own
 *
 * The open rung is gone, and its absence is the substantive change here. Until 2026-09-22 the set
 * stepped both fill and rim one rung up while open; it no longer does, and open and closed are
 * pixel-identical in colour on every surface. `accordionContrast.test.ts` asserts that as the
 * inverse of the case it used to assert.
 *
 * Source order is not what resolves the remaining pair. `:has(…:hover)` carries the specificity of
 * its argument, and hover carries `:not(:active)` so the two can never both match. That mutual
 * exclusion is the same guard the state layer uses, and it is deliberate: active is the LESS
 * specific of the pair and would otherwise lose.
 */
export const accordionItemRecipe = cva(
  [
    'relative flex w-full flex-col',
    'isolate',
    'transition-[border-color,background-color] duration-[var(--motion-control)] ease-[var(--motion-control-ease)] motion-reduce:transition-none',
    focusRing('accordionItem'),
    '[&[data-disabled]>[data-part=header]]:opacity-50',
    '[&[data-disabled]>[data-part=panel]>[data-part=panel-content]]:opacity-50',
  ],
  {
    variants: {
      layout: {
        inline: 'rounded-[16px]',
        stacked: 'rounded-[24px]',
      },
      surface: {
        /**
         * The boxed card. The fill and the rim are both painted, and the rim steps on hover and
         * on active — the fill does not, because the fill's interaction layer belongs to the
         * trigger now. See `accordionTriggerRecipe`.
         *
         * No `[data-open]` rule of any kind. The reworked set draws an open card in exactly the
         * resting fill and rim, so the only thing open changes here is the root's gap.
         */
        filled: [
          'border-solid border-[length:var(--ds-accordion-stroke-width)] border-[var(--ds-accordion-stroke)]',
          'bg-[var(--ds-accordion-bg)]',
          '[&:has(>[data-part=header]>[data-part=trigger]:not([data-disabled]):not(:active):hover)]:border-[var(--ds-accordion-stroke-hover)]',
          '[&:has(>[data-part=header]>[data-part=trigger]:not([data-disabled]):active)]:border-[var(--ds-accordion-stroke-active)]',
        ],
        /**
         * No card: no fill and no rim at rest, and nothing to step on hover either. The bare
         * surface's whole interaction treatment is the trigger's rimmed chip.
         *
         * The item keeps the layout radius even with nothing to draw, because the focus halo is a
         * box-shadow and traces the item's own corners — the set's no-surface root keeps its 16px
         * for the same reason. Note the set does NOT draw a focus ring on this surface at all: its
         * no-surface Focus cells have no ring carrier, which for a row that is a real button is an
         * omission in the drawing rather than a decision, so the shared halo stays on here.
         */
        none: 'border-0 bg-transparent',
      },
    },
    defaultVariants: { layout: 'inline', surface: 'filled' },
  }
);

/**
 * The heading that wraps the trigger.
 *
 * Base UI renders an `<h3>`, which is a real heading in the document outline — that is the whole
 * point of the part, since it is what lets a screen reader user jump between sections. It carries
 * no type styling of its own: the visible text is the trigger's, and styling the heading would
 * fight it. `m-0` is load-bearing rather than tidiness, for the reason the field's helper row
 * gives: Preflight is absent from the token layer, so an `<h3>` keeps the browser's default
 * `margin: 1em 0` and the card would open with 21px of empty space above and below its row.
 */
export const accordionHeaderRecipe = cva(['relative z-[1] m-0 flex']);

/**
 * The row, which is the control.
 *
 * It fills the card and pays all the padding itself, the same decision the field's control makes
 * and for the same reason: pad the card instead and the button becomes a strip across the middle,
 * so most of what looks like the row is dead to the pointer. Here the whole row is meant to open
 * the section, so every pixel of it has to be the button. In `layout="stacked"` that means BOTH
 * rows — the eyebrow and the title beneath it — are inside one button.
 *
 * ── The resets ────────────────────────────────────────────────────────────────────────────────
 *
 * Preflight is deliberately absent from the token layer, so the browser's own `<button>` styling
 * is still in play: a grey bevelled background, a 2px outset border, centred 13.33px Arial and
 * `text-align: center`. Every one of those is overridden explicitly below. `text-left` is the one
 * that is easy to miss and the most visible when missed — a centred section title does not line up
 * with the panel text underneath it.
 *
 * ── The insets, and the bottom one that moves ─────────────────────────────────────────────────
 *
 * Read off the set: `x5` (20px) all round for stacked, `x4` (16px) for inline, and `surface="none"`
 * overriding both to `x2` (8px) horizontally and `x3` (12px) vertically.
 *
 * The stacked value is `x5` and NOT the `x6` (24px) the set description claims. Measured off the
 * variant roots on 2026-09-22: every stacked cell insets 20. The drawing wins, so the code moved
 * 24 → 20 and the Figma description was corrected to match. The radius is unaffected — 24px on the
 * card, 23px inside its 1px rim.
 *
 * The bottom inset SHRINKS to the 12px inter-row gap while the panel is open, and that is arithmetic
 * from the cells rather than a flourish. A closed stacked card measures 140px: 1 + 20 + 98 + 20 + 1.
 * Its open sibling measures 152px: 1 + 20 + 98 + 12 + 0 + 20 + 1 — the header is unchanged and the
 * 20px that was the trigger's bottom padding has become a 12px gap, with the 20px reappearing below
 * the panel's content. Inline works the same way from 16px (98 closed, 110 open). `surface="none"`
 * keeps its 12px bottom inset and adds an 8px gap, so nothing moves there.
 *
 * ── The stacked row gap, which was wrong ──────────────────────────────────────────────────────
 *
 * 16px (`x4`) between the eyebrow row and the title block, which is the `Header` frame's own item
 * spacing in every stacked cell. It shipped as 36px, a value carried over from a header layer the
 * rework deleted; nothing compensated for it, so a stacked card simply had 20px too much air in
 * the middle.
 *
 * Focus is intentionally absent here. The latest set draws the shared two-shadow Focused effect
 * around the whole item, so `accordionItemRecipe` promotes and rings the parent when its direct
 * trigger is focus-visible. A nested button in panel content must not ring the accordion item.
 *
 * ── Radius ────────────────────────────────────────────────────────────────────────────────────
 *
 * The card's radius less its 1px border — 23px stacked, 15px inline — which is the inner radius
 * that leaves the hover fill concentric with the edge rather than showing a sliver of card colour
 * at each corner. The bottom pair is squared off while the panel is open, because the row is then
 * the top half of the card and a rounded bottom edge would cut a notch out of the panel above its
 * own content. The set draws exactly that: its open overlays measure 23/23/0/0 stacked and 15/15/0/0
 * on both inline surfaces (verified 2026-09-22).
 *
 * ── The state layer, which the 2026-09-22 rework moved HERE from the item ─────────────────────
 *
 * This is the one structural change in that port, and it is not cosmetic.
 *
 * The overlay used to be a `::before` on the whole ITEM, gated by
 * `:has(> [data-part=header] > [data-part=trigger]:hover)`, so the layer spanned the header AND an
 * open panel. At 8% translucent that difference is invisible. With the reworked set's OPAQUE
 * `--background-primary-hover` fill it would mean hovering an open accordion repaints the whole
 * card, panel content background included.
 *
 * The drawing says the trigger. Measured off the set on 2026-09-22: the overlay is an ABSOLUTE
 * layer whose box is the clickable row, not the card — 418×96 inside a 420×98 inline root, and it
 * keeps the CLOSED card's height on the open cells, so it never covers the panel. Its parent in the
 * layer tree is `Header`, but an absolutely-positioned child's parent does not determine its box,
 * and the box is what matters.
 *
 * The trigger is also the element that already has everything the layer needs — both variants, the
 * correct radius per layout, the squared bottom while open, and the hover itself, since it IS the
 * hovered element. So the four state selectors collapse from the `:has()` indirection to plain
 * pseudo-classes and the recipe gets simpler rather than more complicated.
 *
 * Three details that are each load-bearing:
 *
 *   `relative isolate`   the trigger was NOT positioned before this. Without `relative` an
 *                        absolutely positioned `::before` resolves against `accordionHeaderRecipe`
 *                        instead — the wrong containing block. `isolate` makes the trigger a
 *                        stacking context so the negative z-index below is contained.
 *   `before:-z-10`       an opaque positioned pseudo-element paints ABOVE unpositioned siblings, so
 *                        without this the fill covers the title. A negative z-index inside a
 *                        stacking context paints above the element's own (transparent) background
 *                        and below its in-flow content, which is exactly the backdrop position.
 *   `before:rounded-[inherit]`  the TRIGGER carries the radius on every surface — 15px inline, 23px
 *                        stacked, bottom pair squared while open — and the layer inherits it.
 *                        Reversed on 2026-09-23 from a layer that took its own radius from the
 *                        layout: nothing transitioned that, so on a hovered row the corners snapped
 *                        square on the first frame of opening. `inherit` resolves from the parent's
 *                        computed value, which mid-transition is the animated one, so the corners
 *                        now follow the trigger's clock with no second animation of their own.
 *
 * ── Opening and closing run on ONE clock ───────────────────────────────────────────────────────
 *
 * The trigger's bottom inset (16 -> 12) and its bottom corners (15 -> 0) change BECAUSE the panel
 * opened, so they transition on the panel's role, `--motion-layout`, and nothing else. They used to
 * read `--motion-control` — the tint role, 150ms and front-loaded — so the card lost its 4px of inset
 * before the panel's height had visibly started: a contraction, then a growth, which read as the
 * card resizing. The `OpeningAndClosing` story samples all three every frame and asserts they
 * travel the same fraction of their distance; the only allowance is the single frame Base UI takes
 * to lift `data-starting-style` before the height moves.
 *
 * `color` left the trigger's transition list at the same time. The trigger paints no text colour of
 * its own — the title, description and indicator each carry theirs and transition it themselves —
 * so the entry animated nothing.
 *
 * The transition and `motion-reduce` pair moved with it. Leaving them on the old owner would have
 * silently disabled reduced-motion handling on the layer that actually animates — no type error, no
 * failing snapshot. `Accordion.test.tsx` asserts the four state classes are emitted here and not on
 * the item, which is the regression guard for the move.
 *
 * `surface="none"`'s interaction BORDER travelled too, for the same reason: it is part of this
 * layer. The bare surface has no root rim to step, so its chip carries its own 1px stroke on the
 * translucent family, which is what the set draws.
 *
 * ── Alignment, which depends on whether there is a description ───────────────────────────────
 *
 * Inline rows are CENTRED when there is no description and TOP-aligned when there is one.
 *
 * Neither alone is right. Top alignment is the only one that survives a description — centre a
 * two-line block and the caret drifts down to the middle of text it is supposed to be pointing
 * at. But on a single-line row it misaligns everything the moment a `leftSlot` Tag is present: the
 * Tag is 32px and the title line 20px, so top-aligned the title's text sits 6px above the Tag's and
 * the caret 6px above both. That was the shipped behaviour until 2026-09-23 and it is what the
 * change fixes.
 *
 * `:has()` rather than a prop, because the description's presence is already in the DOM and a
 * second source of truth would be one more thing to keep in step. Figma cannot express this: an
 * auto-layout alignment cannot be bound to a boolean property, so the set draws one alignment and
 * its description records that the code switches.
 */
export const accordionTriggerRecipe = cva(
  [
    'group/accordion-trigger',
    'relative isolate',
    'm-0 flex w-full appearance-none border-0 bg-transparent text-left outline-none',
    'font-sans',
    'cursor-pointer select-none',
    'transition-[padding-bottom,border-radius] duration-[var(--motion-layout)] ease-[var(--motion-layout-ease)] motion-reduce:transition-none',

    'before:pointer-events-none before:absolute before:inset-0 before:-z-10 before:rounded-[inherit] before:opacity-0 before:content-[" "]',
    'before:transition-[background-color,border-color,opacity] before:duration-[var(--motion-control)] before:ease-[var(--motion-control-ease)] motion-reduce:before:transition-none',
    '[&:not([data-disabled]):not(:active):hover::before]:bg-[var(--ds-accordion-state-hover)]',
    '[&:not([data-disabled]):not(:active):hover::before]:opacity-100',
    '[&:not([data-disabled]):active::before]:bg-[var(--ds-accordion-state-active)]',
    '[&:not([data-disabled]):active::before]:opacity-100',

    '[&[data-disabled]]:cursor-not-allowed',
  ],
  {
    variants: {
      layout: {
        inline: [
          'flex-row items-center gap-[12px] rounded-[15px] has-[[data-part=description]]:items-start',
          'data-[panel-open]:rounded-b-none',
        ],
        stacked: ['flex-col items-stretch gap-[16px] rounded-[23px]', 'data-[panel-open]:rounded-b-none'],
      },
      surface: {
        filled: '',
        // The chip's own rim, which is all a bare row has to mark itself with — there is no card
        // rim here to step. Transparent at rest so the layer is a single element in both states.
        none: [
          'px-[8px] py-[12px]',
          'before:border-solid before:border-[length:var(--ds-accordion-stroke-width)] before:border-transparent',
          '[&:not([data-disabled]):not(:active):hover::before]:border-[var(--ds-accordion-stroke-hover)]',
          '[&:not([data-disabled]):active::before]:border-[var(--ds-accordion-stroke-active)]',
        ],
      },
    },
    compoundVariants: [
      {
        layout: 'inline',
        surface: 'filled',
        class: ['p-[16px]', 'data-[panel-open]:pb-[12px]'],
      },
      {
        layout: 'stacked',
        surface: 'filled',
        class: ['p-[20px]', 'data-[panel-open]:pb-[12px]'],
      },
    ],
    defaultVariants: { layout: 'inline', surface: 'filled' },
  }
);

/**
 * The eyebrow row, `layout="stacked"` only — Figma's `Row_Meta`.
 *
 * The left slot on one side and the actions on the other, 32px tall because that is the height
 * of the `Tag` the set puts there. Centred rather than top-aligned, unlike the inline row: this row
 * holds only single-height objects, so there is no two-line case to align against.
 */
export const accordionMetaRowRecipe = cva(['flex w-full items-center gap-[12px]']);

/**
 * The trailing cluster inside the eyebrow row — Figma's `Actions`.
 *
 * `flex-1` with `justify-end`, so it takes whatever the left slot leaves and pushes its contents
 * against the card's inner edge. `min-w-0` for the reason the title block needs it: a flex item
 * refuses to shrink below its content by default, and a long `rightSlot` value would otherwise push
 * the left tag out through the opposite edge.
 */
export const accordionActionsRecipe = cva(['flex min-w-0 flex-1 items-center justify-end gap-[12px]']);

/**
 * The open/closed indicator's 20px box.
 *
 * A 20px wrapper around a 16px glyph, which is the set's `Indicator_Leading` and
 * `Indicator_Trailing` frames exactly. The 2px of slack on each side is not decoration: it is what
 * the inline panel content's 32px inset is computed from — the wrapper's 20 plus the row's 12 gap —
 * so the columns line up by construction rather than by a number someone chose.
 *
 * Reads the trigger's state through `group/accordion-trigger`, being a child with no state of its
 * own, which is what a group is for.
 *
 * ── Rotation lives on the glyph, not here ─────────────────────────────────────────────────────
 *
 * This wrapper transitions COLOUR only, which is what carries hover and disabled. The open/closed
 * turn is `accordionGlyphRecipe`'s, on the `<g>` inside the SVG, so each element keeps a single
 * motion role: colour on the control clock here, rotation on the panel's clock there.
 */
export const accordionIndicatorRecipe = cva(
  [
    'flex size-[20px] shrink-0 items-center justify-center',
    'text-[var(--ds-accordion-icon)]',
    'transition-[color] duration-[var(--motion-control)] ease-[var(--motion-control-ease)] motion-reduce:transition-none',
  ],
  {
    variants: {
      /**
       * Only the inline arrangement can move: the stacked indicator's place is the eyebrow row's
       * trailing edge, which is a different element entirely, so there is nothing to order.
       *
       * The rule keys off `end`, NOT `start`, and the asymmetry is the whole subtlety. The DOM
       * order here is the SET's — indicator, left slot, title, right slot — so the glyph is already
       * first and `start` needs no rule at all. It was written the other way round first, keyed off
       * `start` with `order-[-1]`, which is a no-op on an element that is already first: the caret
       * rendered on the leading edge for BOTH values of the prop, and every unit test passed
       * because they assert the attribute and the rule rather than the geometry. The `IconPlacement`
       * story's `play` function measures the two boxes, and a browser screenshot is what actually
       * caught it.
       *
       * `order-[1]` rather than `order-last`, which Tailwind v4 compiles to
       * `order: calc(infinity)`. One explicit step above the default 0 is all that is needed to put
       * the glyph after three siblings that never set `order` themselves, and it is a value a reader
       * can evaluate.
       */
      layout: {
        inline: 'group-data-[icon-placement=end]/accordion:order-[1]',
        stacked: '',
      },
    },
    defaultVariants: { layout: 'inline' },
  }
);

/**
 * The glyph inside the indicator: turns between the two cells the set draws for each layout.
 *
 * Inline `CaretRight` rotates 90deg onto `CaretDown`, exactly. Stacked `Plus` rotates 45deg AND
 * scales by 9√2 / 11 ≈ 1.1571, because a rotated Plus alone is a 7.78px X and the set draws 9×9; the
 * paths carry `non-scaling-stroke` so the 1.2px weight survives the scale. See `Accordion.tsx`.
 *
 * On the PANEL's clock, `--motion-layout`, on purpose: the glyph turns because the panel opened,
 * like the trigger's inset and corners, and the `OpeningAndClosing` story asserts all of them
 * travel the same fraction per frame.
 *
 * `transform-box: view-box` with an 8px 8px origin puts the pivot at the glyph's centre in the
 * 16-unit viewBox. An SVG child otherwise pivots on the viewport's 0 0 and swings off its box.
 *
 * Tailwind v4's `rotate-*` and `scale-*` write the individual `rotate` and `scale` properties, not
 * `transform`, so the transition list names those two.
 *
 * An implementation before the 2026-09-18 port rotated one `CaretDown` 180deg up and down — a
 * different gesture on a different glyph. This lands on the drawn cells at both ends.
 */
export const accordionGlyphRecipe = cva(
  [
    '[transform-box:view-box] origin-[8px_8px]',
    'transition-[rotate,scale] duration-[var(--motion-layout)] ease-[var(--motion-layout-ease)] motion-reduce:transition-none',
  ],
  {
    variants: {
      layout: {
        inline: 'group-data-[panel-open]/accordion-trigger:rotate-90',
        stacked: [
          'group-data-[panel-open]/accordion-trigger:rotate-45',
          'group-data-[panel-open]/accordion-trigger:scale-[1.1571]',
        ],
      },
    },
    defaultVariants: { layout: 'inline' },
  }
);

/** The left slot's wrapper — a `Tag` by default, so it must not be allowed to shrink. */
export const accordionLeftSlotRecipe = cva(['flex shrink-0 items-center']);

/** The right slot's wrapper. Non-interactive content only — see `types.ts` for why. */
export const accordionRightSlotRecipe = cva(['flex shrink-0 items-center']);

/**
 * The title and description as one block — Figma's `Titles`.
 *
 * `min-w-0` is what lets a long title wrap instead of forcing the row wider than the card: a flex
 * item's default `min-width: auto` refuses to shrink below its content, and the indicator would be
 * pushed out through the edge. The title wraps rather than truncating, because a section title is
 * the only thing naming the content behind it and half of one is not a name.
 *
 * 4px between the two lines, which is `x1` in the set.
 */
export const accordionTitlesRecipe = cva(['flex min-w-0 flex-1 flex-col gap-[4px] text-left']);

/**
 * The title.
 *
 * Two type levels, one per layout, and this is NOT an inconsistency in the set even though it reads
 * like one at first: stacked draws `Heading/xs` (16/26) and inline draws `Label/lg` (14/20).
 * Measured from the cells on 2026-09-18 — the stacked title box is 26px tall and the inline one is
 * 20px — so the set's written rule that "the row title" is `Label/lg` is describing the inline case
 * only.
 *
 * The reason it is coherent: a stacked card is a section with an eyebrow above it and a description
 * below, which is a heading's job, while an inline row is one line in a list, which is a label's.
 * The set's third type level — anything inside the panel — stays at `Body/md`, so the three remain
 * distinct in both layouts.
 */
export const accordionTitleRecipe = cva(
  [
    'text-[var(--ds-accordion-title)]',
    'transition-[color] duration-[var(--motion-control)] ease-[var(--motion-control-ease)] motion-reduce:transition-none',
  ],
  {
    variants: {
      layout: {
        inline: 'text-label-lg',
        stacked: 'text-heading-xs',
      },
    },
    defaultVariants: { layout: 'inline' },
  }
);

/**
 * The description under the title.
 *
 * `Body/md` in both layouts, and secondary rather than primary — it is subordinate to the title it
 * explains, and the worst state-matched cell is 5.70:1 (dark resting) against a 4.5:1 floor
 * (re-measured 2026-09-23), so the step down is affordable.
 *
 * Secondary and not tertiary, deliberately: this is text a user is meant to READ, and a tertiary
 * foreground is a state signal — disabled, done, empty, or purely decorative — never a way to rank
 * active content. DS 2.0 has no tertiary foreground to reach for anyway, which is the gap that makes
 * the mistake unavailable here; if one ever ships it should be documented that way at birth.
 *
 * The description steps with its trigger row via `group/accordion-trigger`: `-hover` and `-active`
 * group selectors read off the trigger's own pseudo-classes (which already drive the indicator and
 * the title's sibling recipes), so the description brightens in step rather than staying at the
 * resting secondary foreground while everything around it moves. Both selectors carry the same
 * `not([data-disabled])` fence the trigger's overlay uses.
 */
export const accordionDescriptionRecipe = cva([
  'text-body-md text-[var(--ds-accordion-description)]',
  'group-[:not([data-disabled]):not(:active):hover]/accordion-trigger:text-[var(--ds-accordion-description-hover)]',
  'group-[:not([data-disabled]):active]/accordion-trigger:text-[var(--ds-accordion-description-active)]',
  'transition-[color] duration-[var(--motion-control)] ease-[var(--motion-control-ease)] motion-reduce:transition-none',
]);

/**
 * The panel.
 *
 * Height, not max-height, and the value is Base UI's own `--accordion-panel-height` — which it
 * measures and sets as an inline custom property on this element, and leaves as `auto` once the
 * transition has settled. Animating `max-height` to a guessed ceiling is the usual shortcut and it
 * is why so many accordions ease slowly through empty space before the content appears.
 *
 * `data-starting-style` and `data-ending-style` are Base UI's two transition hooks: the first is
 * present for one frame as the panel mounts, the second for the duration of the close. Both pin
 * the height to 0, so the transition runs between 0 and the measured height in each direction. The
 * close hook is also what keeps the content mounted long enough to animate out — Base UI defers
 * the unmount until the animations on this element have finished.
 *
 * `overflow-hidden` belongs here rather than on the card, because this is the element whose height
 * is being clipped.
 *
 * ── Reduced motion ────────────────────────────────────────────────────────────────────────────
 *
 * `motion-reduce:transition-none`, which is not the throwaway it is on the other recipes in this
 * repo. This is the only animated primitive of the five, and an expanding height is a vestibular
 * trigger — WCAG 2.3.3 asks for exactly this. With the transition off the panel snaps to its
 * height instead, and the unmount still happens: Base UI waits for the animations on the element
 * to finish, and an element with no transition has none, so the wait resolves immediately.
 */
export const accordionPanelRecipe = cva([
  'relative z-[1] h-[var(--accordion-panel-height)] overflow-hidden',
  /**
   * `--motion-layout`, which is what this declaration should always have read: a panel expanding is
   * an on-screen element MOVING, not a control changing its own tint, and the role table names
   * exactly that case. It had been left on Tailwind's bare ease-out curve and `--motion-control` as
   * the single allowlisted literal in `motionLiterals.test.ts`, because no curve then available
   * equalled that one and the change is visible.
   *
   * It is taken here, deliberately, and it is the one retime in this change:
   *
   *   duration   150ms -> 200ms. A height expansion travels further than any tint in the set and is
   *              the one declaration whose length a reader actually perceives.
   *   curve      Tailwind's ease-out (`cubic-bezier(0, 0, 0.2, 1)`) -> `--ease-movement`, which is
   *              ease-in-out-cubic. A disclosure is a two-way toggle between two on-screen states,
   *              so it accelerates out of rest and brakes into it. A pure deceleration is the right
   *              curve for something ARRIVING from off-screen, which a panel already in the flow is
   *              not.
   *
   * The allowlist is now empty and the guard carries no exception at all.
   *
   * ── The one layout-tier animation in the set, and why it stays ────────────────────────────────
   *
   * `height` enters the renderer at Layout, so it re-measures every frame — the most expensive tier
   * there is, where every other animated declaration in DS 2.0 is composite-only (`opacity`,
   * `scale`) or paint-tier at worst. The usual swap down a tier is `scale-y`, and it is not taken:
   * scaling a panel distorts the type inside it for the whole 200ms, which is worse than the cost
   * it saves. This is a knowing exception, not an oversight.
   *
   * What keeps it affordable is what is being measured. The height comes from Base UI as an inline
   * custom property on THIS element, set once per open, and the transition then runs on `height`
   * directly. That is not the trap it resembles: an animated custom property on a SHARED PARENT
   * forces a style recalculation on every descendant every frame, which is what makes a long
   * drag-driven list degrade with its item count. Here nothing animates the variable, and the
   * element is the only consumer.
   *
   * No `will-change`. Browsers already promote what they can, a layer costs GPU memory, and the
   * rule is to add it only once a shift or a dropped frame has actually been seen. None has.
   */
  'transition-[height] duration-[var(--motion-layout)] ease-[var(--motion-layout-ease)]',
  'motion-reduce:transition-none',
  'data-[starting-style]:h-0 data-[ending-style]:h-0',
]);

/**
 * The panel's content wrapper.
 *
 * A separate element from the panel, and it has to be. The panel's height is animated to a measured
 * value, so padding on it would be added to that height at every frame and the collapsed state
 * would still be `padding-block` tall rather than zero — the content would disappear into a
 * stubborn empty band. Paying the padding one level in keeps the animated box exactly the height of
 * what is inside it.
 *
 * The top padding is the inter-row gap the trigger's bottom inset shrank to make room for — 12px on
 * the boxed surface, 8px on `none` — and it is paid HERE rather than as a `gap` on the item for
 * the same reason the rest of the padding is: a flex gap on the item is paid for the whole duration
 * of the CLOSING animation, while the panel is still mounted at zero height, so the card would hold
 * a 12px band of empty space on its way shut.
 *
 * ── The inline inset ──────────────────────────────────────────────────────────────────────────
 *
 * The inline panel content starts under the TITLE rather than under the caret. Its start inset is
 * the surface inset plus the indicator's 20px wrapper and the row's 12px gap: 48px on the boxed
 * surface and 40px on `none`. Keeping the arithmetic here makes the two values move together if
 * either Figma token changes again.
 *
 * It applies only when the indicator is actually leading. With the default `iconPlacement="end"`
 * there is nothing at the row's start to clear, so the inset would push the content 32px past the
 * title it is supposed to line up with — hence the `group-data` guard rather than a flat value.
 * `layout="stacked"` never takes it: its title starts at the card's own inset, because the
 * indicator is on the row above.
 */
export const accordionPanelContentRecipe = cva(['font-sans text-body-md text-[var(--ds-accordion-panel-fg)]'], {
  variants: {
    layout: {
      inline: '',
      stacked: '',
    },
    surface: {
      filled: '',
      none: 'px-[8px] pt-[8px] pb-[12px]',
    },
  },
  compoundVariants: [
    {
      layout: 'inline',
      surface: 'filled',
      class: 'px-[16px] pt-[12px] pb-[16px] group-data-[icon-placement=start]/accordion:ps-[48px]',
    },
    {
      layout: 'inline',
      surface: 'none',
      class: 'group-data-[icon-placement=start]/accordion:ps-[40px]',
    },
    {
      layout: 'stacked',
      surface: 'filled',
      class: 'px-[20px] pt-[12px] pb-[20px]',
    },
  ],
  defaultVariants: { layout: 'inline', surface: 'filled' },
});
